import classNames from "classnames";
import {
  JSXElementConstructor,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { thumbHashToDataURL } from "thumbhash";
import * as styles from "./image-with-placeholder.module.css";

export interface ImageData {
  url?: string | null;
  thumbhash?: string | null;
  altText?: string | null;
}

export interface ImageWithPlaceholderProps {
  image: ImageData | null | undefined;
  alt?: string;
  width?: number;
  height?: number;
  DefaultIcon?: JSXElementConstructor<any>;
  className?: string;
}

function decodeThumbhash(thumbhash: string): string | null {
  try {
    const bytes = Uint8Array.from(atob(thumbhash), (c) => c.charCodeAt(0));
    return thumbHashToDataURL(bytes);
  } catch {
    return null;
  }
}

function buildImageUrl(
  baseUrl: string,
  width?: number,
  height?: number,
): string {
  if (!width && !height) {
    return baseUrl;
  }
  const url = new URL(baseUrl);
  if (width) {
    url.searchParams.set("width", String(width * 4));
  }
  if (height) {
    url.searchParams.set("height", String(height * 4));
  }
  return url.toString();
}

/**
 * Image component with thumbhash placeholder support.
 * Shows a blurred placeholder while the image loads, then transitions to the full image.
 *
 * @param image - Image data object with url, thumbhash, and altText
 * @param alt - Alt text override (falls back to image.altText)
 * @param width - Width in pixels (sets container size and adds ?width= to URL)
 * @param height - Height in pixels (sets container size and adds ?height= to URL)
 * @param className - Additional classes for the container
 */
export const ImageWithPlaceholder = memo(function ImageWithPlaceholder({
  image,
  alt,
  width,
  height,
  DefaultIcon,
  className,
}: ImageWithPlaceholderProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const imageUrl = image?.url;
  const thumbhash = image?.thumbhash;
  const altText = alt ?? image?.altText ?? "";

  const placeholderUrl = useMemo(() => {
    if (thumbhash) {
      return decodeThumbhash(thumbhash);
    }
    return null;
  }, [thumbhash]);

  const finalImageUrl = useMemo(() => {
    if (!imageUrl) {
      return null;
    }
    return buildImageUrl(imageUrl, width, height);
  }, [imageUrl, width, height]);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [finalImageUrl]);

  const showPlaceholder = placeholderUrl && !imageLoaded && !imageError;
  const showImage = finalImageUrl && !imageError;
  const showFallback = imageError || (!finalImageUrl && !placeholderUrl);

  const containerStyle = useMemo(() => {
    if (!width && !height) {
      return undefined;
    }
    return { width, height };
  }, [width, height]);

  return (
    <div
      className={classNames(styles.container, className)}
      data-web="ImageWithPlaceholder"
      style={containerStyle}
    >
      {showPlaceholder && (
        <img
          alt=""
          aria-hidden="true"
          className={classNames(styles.placeholder, {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [styles.hidden]: imageLoaded,
          })}
          src={placeholderUrl}
        />
      )}
      {showImage && (
        <img
          alt={altText}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          className={classNames(styles.image, { [styles.loaded]: imageLoaded })}
          onError={() => setImageError(true)}
          onLoad={() => setImageLoaded(true)}
          src={finalImageUrl}
        />
      )}
      {showFallback && DefaultIcon && (
        <div className={styles.fallback}>
          <DefaultIcon className={styles.fallbackIcon} />
        </div>
      )}
    </div>
  );
});
